import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { toCents } from "@/lib/format";
import { PAYMENT_METHODS } from "@/lib/bookingConstants";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { FieldSelect } from "@/components/ui/field-select";
import { ReceiptField } from "./ReceiptField";
import { Loader2 } from "lucide-react";

const input = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";
const label = "mb-1 block text-sm font-medium text-slate-700";
const todayIso = () => new Date().toLocaleDateString("en-CA"); // local-tz YYYY-MM-DD

export default function ExpenseFormDrawer({ open, onOpenChange, currency }) {
  const { orgId } = useAuth();
  const qc = useQueryClient();
  const { data: catData } = useQuery({
    queryKey: ["expense-categories", orgId],
    queryFn: async () => (await api.get("/api/expense-categories", { params: { organization_id: orgId } })).data,
    enabled: open,
  });
  const cats = (catData?.categories || []).filter((c) => c.is_active);

  const [categoryId, setCategoryId] = useState("");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [date, setDate] = useState(todayIso());
  const [supplier, setSupplier] = useState("");
  const [desc, setDesc] = useState("");
  const [receipt, setReceipt] = useState(null);

  const reset = () => { setCategoryId(""); setAmount(""); setMethod("cash"); setDate(todayIso()); setSupplier(""); setDesc(""); setReceipt(null); };

  const mut = useMutation({
    mutationFn: async () => (await api.post("/api/expenses", {
      category_id: categoryId, amount_cents: toCents(amount), payment_method: method,
      expense_date: date, supplier_or_recipient: supplier || null, description: desc || null,
      receipt_file_id: receipt?.id || null,
    }, { params: { organization_id: orgId } })).data,
    onSuccess: (d) => {
      if (d.warning) toast.warning(d.warning); else toast.success(`Dépense ${d.expense_reference} enregistrée`);
      qc.invalidateQueries(); onOpenChange(false); reset();
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });

  const valid = categoryId && amount !== "" && date;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" aria-describedby={undefined} className="w-full overflow-y-auto p-0 sm:max-w-md" data-testid="expense-form-drawer">
        <SheetHeader className="border-b border-slate-100 px-6 py-4">
          <SheetTitle>Nouvelle dépense</SheetTitle>
          <SheetDescription className="sr-only">Enregistrez une dépense opérationnelle.</SheetDescription>
        </SheetHeader>
        <div className="space-y-4 p-6">
          <div><label className={label}>Catégorie</label>
            <FieldSelect testId="expense-category-select" className="w-full" value={categoryId} onChange={setCategoryId}
              placeholder="Choisir une catégorie" options={cats.map((c) => ({ value: c.id, label: c.name }))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Montant ({currency})</label>
              <input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="450" data-testid="expense-amount-input" className={input} />
            </div>
            <div><label className={label}>Mode de paiement</label>
              <FieldSelect testId="expense-method-select" className="w-full" value={method} onChange={setMethod}
                options={Object.entries(PAYMENT_METHODS).map(([k, v]) => ({ value: k, label: v }))} />
            </div>
          </div>
          <div><label className={label}>Date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="expense-date-input" className={input} />
          </div>
          <div><label className={label}>Fournisseur / bénéficiaire (optionnel)</label>
            <input value={supplier} onChange={(e) => setSupplier(e.target.value)} data-testid="expense-supplier-input" className={input} />
          </div>
          <div><label className={label}>Description (optionnel)</label>
            <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={2} data-testid="expense-description-input" className={input} />
          </div>
          <div><label className={label}>Justificatif (optionnel)</label>
            <ReceiptField value={receipt} onChange={setReceipt} />
          </div>
          <button disabled={mut.isPending || !valid} onClick={() => mut.mutate()} data-testid="save-expense-button"
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Enregistrer la dépense
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
